import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';

export interface IClientApiDto {
  id: number;
  code?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  isActive?: boolean;
  companyId?: number;
  createdAt?: string;
}

export interface IClientListResponseDto {
  list: IClientApiDto[];
  total: number;
  totalPages: number;
}

export interface IClientListQueryDto {
  page?: number;
  limit?: number;
  search?: string;
}

export interface IInvitationMetadata {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface IInvitationApiDto {
  id: string;
  entityType: string;
  entityId: number;
  email?: string | null;
  state: string;
  channel: string;
  metadata: IInvitationMetadata;
  createdAt: string;
  expiresAt: string;
  approvedAt?: string;
}

export interface IInvitationListResponseDto {
  invitations: IInvitationApiDto[];
  total: number;
}

export interface IInvitationListQueryDto {
  entityType?: string;
  state?: string;
  limit?: number;
  offset?: number;
}

export interface ICreateClientDto {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  type: 'individual' | 'business';
  name?: string;
  language?: string;
}

export interface ICreateInvitationResponseDto {
  invitationId: string;
  state: string;
  expiresAt: string;
}

export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Clients', 'Invitations'],
  endpoints: build => ({
    getClientList: build.query<IClientListResponseDto, IClientListQueryDto>({
      query: params => ({
        url: '/system/client/list',
        method: 'GET',
        params,
      }),
      transformResponse: (response: { data: IClientListResponseDto }) => response.data,
      providesTags: ['Clients'],
    }),

    getInvitationsList: build.query<IInvitationListResponseDto, IInvitationListQueryDto>({
      query: params => ({
        url: '/system/invitations',
        method: 'GET',
        params,
      }),
      transformResponse: (response: { data: IInvitationListResponseDto }) => response.data,
      providesTags: ['Invitations'],
    }),

    createClient: build.mutation<ICreateInvitationResponseDto, ICreateClientDto>({
      query: body => ({
        url: '/system/invitations/create',
        method: 'POST',
        body: {
          entityType: 'admin',
          entityId: 0,
          roleType: 'admin',
          channel: 'EMAIL',
          email: body.email,
          firstName: body.firstName,
          lastName: body.lastName,
          language: body.language,
          metadata: {
            source: 'owner-clients-modal',
            type: body.type,
            name: body.name,
            language: body.language,
          },
        },
      }),
      transformResponse: (response: { data: ICreateInvitationResponseDto }) => response.data,
    }),

    updateClient: build.mutation<IClientApiDto, { id: number; body: Partial<IClientApiDto> }>({
      query: ({ id, body }) => ({
        url: `/system/client/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: { data: IClientApiDto }) => response.data,
      invalidatesTags: ['Clients'],
    }),

    removeClient: build.mutation<void, number>({
      query: id => ({
        url: `/system/client/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Clients'],
    }),

    resendInvitation: build.mutation<{ message: string }, { id: string; language?: string }>({
      query: ({ id, language }) => ({
        url: `/system/invitations/${id}/resend`,
        method: 'POST',
        body: { language },
      }),
      invalidatesTags: ['Invitations'],
    }),

    revokeInvitation: build.mutation<{ message: string }, string>({
      query: id => ({
        url: `/system/invitations/${id}/revoke`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Invitations'],
    }),

    deleteInvitation: build.mutation<{ message: string }, string>({
      query: id => ({
        url: `/system/invitations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invitations'],
    }),
  }),
});

export const {
  useGetClientListQuery,
  useGetInvitationsListQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useRemoveClientMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useDeleteInvitationMutation,
} = clientsApi;
